/**
 * Login / App Entry Gate
 * ----------------------
 * Responsibilities:
 * - Initializes Keycloak (SSO check) and determines authentication state.
 * - Bootstraps the user record in the backend:
 *   - If /users/me exists, we use it to populate a display name.
 *   - If it fails, we attempt to create a user from the JWT (POST /users with empty body),
 *     unless the platform could not be reached at all.
 *   - If that fails too, it shows the error page with a retry, not a spinner.
 * - Loads role information, which decides which rows the one shell carries.
 * - Wraps the app in shared providers (UserPreferences, Localization, Theme).
 */

// TODO: Create a provider for auth context and decouple auth logic from this component.

import { WebSocketProvider } from '../providers/WebSocketProvider';
import { lazy, Suspense, useEffect, useState, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Container, Box, Paper, Stack, Text, Button, readableShade, useTheme } from '../ui';
import { MarkLoader } from './brand/MarkLoader';
import { kc } from '../auth/keycloak';
import {
  forgetReturnPath,
  initRedirectUri,
  rememberReturnPath,
  takeReturnPath,
} from '../auth/returnPath';
import client from '../api/client';
import { getUserRoles, TenantRole, type UserRoles } from '../api/admin';
import type { ColourProfile, TenantLook } from '../api/colourProfiles';
import {
  fallbackNavigation,
  getMyPages,
  type NavGroupInfo,
  type NavPageInfo,
} from '../api/pages';
import { ADMIN_CORE_PAGE_KEYS, CORE_PAGE_KEYS, PageKey } from '../router/pageRegistry';
import { useTranslation } from 'react-i18next';
import '../i18n';
import DevModeBadge from './DevModeBadge';
import { ErrorFallback } from './AppErrorBoundary';
import { isConnectivityError } from '../monitoring/connectivity';
import { captureException } from '../monitoring/sentry';
import { BrandCard } from './brand/BrandCard';
import { StartingScreen } from './brand/StartingScreen';
import { useLook } from '../contexts/useLook';

export type AuthContext = {
  firstName: string;
  tenantRoles: TenantRole[];
  /** The tenants this person is a member of, whether or not they hold a role there. */
  memberTenantIds: readonly number[];
  onLogout: () => void;
  isGlobalAdmin: boolean;
  /**
   * Pages this user may see, granted per system role by a Global Admin.
   * The shells filter their navigation with it and the router redirects away
   * from anything absent.
   */
  allowedPages: ReadonlySet<PageKey>;
  /**
   * How those pages are arranged: which are rows of their own and which are
   * tabs of a shared row. From the same answer as `allowedPages`, so the
   * sidebar can never group a page the router would refuse.
   *
   * Empty is a legitimate value and means "no grouping known" — a failed
   * lookup, or a build talking to a server from before V57. The shells then
   * render every page they hold as its own row, which is what they did before
   * groups existed.
   */
  navigation: { groups: NavGroupInfo[]; layout: NavPageInfo[] };
  /** Per tenant this person is in: its symbol and the colour profile in effect for them there. */
  looks: TenantLook[];
  /** The profile in effect in the platform plane and under no tenant; null is Meridian. */
  lookOutsideTenant: ColourProfile | null;
  /** Ask again after a change to a look, so the shell shows it without a reload. */
  reloadLooks: () => Promise<void>;
};

const ProfilePage = lazy(() => import('./ProfilePage/ProfilePage'));

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Meridian's sign-in buttons stay its own; a remembered profile with a secondary puts both in it.
  const signInTone = useLook().colours.secondary ? 'secondary' : 'primary';
  const { palette } = useTheme();

  // Authentication + bootstrapping state.
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // User-facing name displayed in the app header; derived from backend user record.
  const [firstName, setFirstName] = useState('');

  // New user flow: if we had to create the user record, we prompt profile completion.
  const [showProfile, setShowProfile] = useState(false);

  // Global admin routing: global admins land on the admin dashboard directly.
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);

  // Tenant roles are passed to the app shell for tenant-scoped navigation/features.
  const [tenantRoles, setTenantRoles] = useState<TenantRole[]>([]);
  const [memberTenantIds, setMemberTenantIds] = useState<number[]>([]);

  // Carried by the same answer as the roles, so the first page is already in them.
  const [looks, setLooks] = useState<TenantLook[]>([]);
  const [lookOutsideTenant, setLookOutsideTenant] = useState<ColourProfile | null>(null);

  const applyLooks = (answer: UserRoles) => {
    setLooks(Array.isArray(answer.looks) ? answer.looks : []);
    setLookOutsideTenant(answer.lookOutsideTenant ?? null);
  };

  const reloadLooks = async () => {
    try {
      applyLooks(await getUserRoles());
    } catch {
      // The look already on screen stays; the next start asks again.
    }
  };

  // Pages this user may see; drives both shells' navigation and the route guards.
  const [allowedPages, setAllowedPages] = useState<ReadonlySet<PageKey>>(
    () => new Set(CORE_PAGE_KEYS),
  );

  // And how they are arranged. Seeded ungrouped from the same core pages, so the
  // shell has rows to render during the one render before /pages/me answers.
  const [navigation, setNavigation] = useState<AuthContext['navigation']>(() =>
    fallbackNavigation(CORE_PAGE_KEYS),
  );

  /** Apply one answer to both, so the two can never be a request apart. */
  const applyPages = (answer: {
    pages: PageKey[];
    groups: NavGroupInfo[];
    layout: NavPageInfo[];
  }) => {
    setAllowedPages(new Set(answer.pages));
    setNavigation({ groups: answer.groups, layout: answer.layout });
  };

  /** What a failed lookup leaves behind: a reduced app, never an empty one. */
  const applyCorePages = (keys: readonly PageKey[]) => {
    setAllowedPages(new Set(keys));
    setNavigation(fallbackNavigation(keys));
  };

  // adminChecked and authChecked are separate to allow a smooth staged UI:
  // - authChecked gates the login screen vs. authenticated branches
  // - adminChecked gates admin routing vs. standard app routing
  const [adminChecked, setAdminChecked] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<Error | null>(null);

  /**
   * React Strict Mode (dev) may invoke effects twice to surface side effects.
   * Keycloak initialization must not run twice, so we guard it with a ref.
   */
  const keycloakInitialized = useRef(false);

  // No admin landing redirect any more. It existed to move a global admin into
  // the other shell; with one shell there is nowhere to send them, and the
  // administration rows are simply in their sidebar. A redirect here would now
  // only take somebody off the page they asked for.

  const failBootstrap = (error: unknown) => {
    const failure = error instanceof Error ? error : new Error(String(error));
    // The tag the two boundaries set, so one filter says which screen a member saw.
    captureException(failure, { tags: { boundary: 'bootstrap' } });
    setBootstrapError(failure);
  };

  /**
   * The user record, roles and pages, once Keycloak has a session. Its own
   * function so the error page can run it again, which kc.init cannot be:
   * keycloak-js refuses a second init on the same instance.
   */
  const bootstrapUser = async () => {
    try {
      /**
       * User bootstrap:
       * Prefer reading the existing user record from the backend.
       * If this request fails (e.g., user doesn't exist yet), we attempt to create it.
       */
      const response = await client.get('/users/me');

      // User exists, set the data
      setFirstName(response.data.firstName);
      setShowProfile(false);

      /**
       * Role lookup:
       * Used to decide whether to render the global admin dashboard or standard app.
       * If role lookup fails, we default to non-admin behavior.
       */
      // Kept as a local as well as in state: the page-access fallback
      // below needs it in this same tick, and a setState is not readable
      // until the next render.
      let globalAdmin = false;
      try {
        const rolesResponse = await getUserRoles();
        globalAdmin = rolesResponse.isGlobalAdmin;
        setIsGlobalAdmin(globalAdmin);
        setTenantRoles(rolesResponse.tenantRoles || []);
        setMemberTenantIds(rolesResponse.memberTenantIds || []);
        applyLooks(rolesResponse);
      } catch {
        setIsGlobalAdmin(false);
        setTenantRoles([]);
        setMemberTenantIds([]);
      }

      /**
       * Page access lookup:
       * Which pages this user's roles have been granted. Resolved in its
       * own try/catch rather than alongside the roles call, so a failure
       * here does not also demote a global admin to a member.
       *
       * On failure we fall back to the core pages instead of an empty
       * set: an empty navigation is indistinguishable from a broken
       * build, whereas a reduced one still lets the user reach a profile
       * and sign out — or, for an admin, the screen that grants pages.
       */
      try {
        applyPages(await getMyPages());
      } catch {
        applyCorePages(globalAdmin ? ADMIN_CORE_PAGE_KEYS : CORE_PAGE_KEYS);
      } finally {
        setAdminChecked(true);
      }
    } catch (error: unknown) {
      // Nothing answered, so there is nobody to create -- and a create would
      // answer 409 for an existing member, or welcome them as a new one.
      if (isConnectivityError(error)) {
        failBootstrap(error);
        return;
      }
      // Any other failure may be a user who does not exist yet.
      try {
        /**
         * Create user with an empty body:
         * Backend derives user identity/profile seed from the JWT token.
         * This avoids duplicating user identity fields in the frontend.
         */
        const createResponse = await client.post('/users', {});

        // Set the data from newly created user
        setFirstName(createResponse.data.firstName);
        setShowProfile(true); // Show profile page for new users

        // Newly created users start without admin/tenant roles until assigned.
        setIsGlobalAdmin(false);
        setTenantRoles([]);
        setMemberTenantIds([]);

        // They do get pages, though: with no role assignment the backend
        // resolves them through the global 'User' role, so this is the
        // call that decides what a brand-new member's app contains.
        try {
          applyPages(await getMyPages());
        } catch {
          applyCorePages(CORE_PAGE_KEYS);
        }

        setAdminChecked(true);
      } catch (createError) {
        failBootstrap(createError);
      }
    }
  };

  useEffect(() => {
    // Prevent double initialization in React Strict Mode
    if (keycloakInitialized.current) {
      return;
    }
    keycloakInitialized.current = true;

    (async () => {
      try {
       // check-sso redirects the whole page, so the route has to be put somewhere
       // that survives it; redirectUri is the origin because that is what the
       // Keycloak client registers. See auth/returnPath.ts.
       rememberReturnPath(window.location, window.sessionStorage);

       // checkLoginIframe is disabled for compatibility with strict browser privacy settings.
       const ok = await kc.init({
         onLoad: 'check-sso',
         redirectUri: initRedirectUri(window.location),
         pkceMethod: 'S256',
         flow: 'standard',
         enableLogging: true,
         checkLoginIframe: false,
        });

        setIsAuthenticated(ok && !!kc.authenticated);
        setAuthChecked(true);

        if (ok && !!kc.authenticated) {
          const back = takeReturnPath(window.sessionStorage);
          if (back) navigate(back, { replace: true });

          await bootstrapUser();
        }
      } catch (err) {
        console.error('Keycloak initialization error:', err);
        // allow retry on error (e.g., transient script load failures)
        keycloakInitialized.current = false;
        setAuthChecked(true);
      }
    })();
    // navigate is stable in react-router 7, and the ref above makes a re-entry
    // a no-op regardless — this effect runs once per mounted session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleLogin = () =>
    kc.login({
      redirectUri: window.location.origin,
    });

  const handleRegister = () =>
    kc.register({
      redirectUri: window.location.origin,
    });

  const handleLogout = () => {
    forgetReturnPath(window.sessionStorage);
    return kc.logout({
      redirectUri: window.location.origin,
    });
  };

  /**
   * New-user profile completion:
   * This is a client-side gate only; backend should still validate required profile fields as needed.
   */
  const handleProfileComplete = () => {
    // After profile is completed, show dashboard
    setShowProfile(false);
  };

  // Clearing the error brings back the Step 4 spinner while the bootstrap runs again.
  const retryBootstrap = () => {
    setBootstrapError(null);
    void bootstrapUser();
  };

  return (
    <>
          {/* Step 1: Wait for Keycloak init / SSO check. */}
          {!authChecked ? (
            <StartingScreen />
          ) : !isAuthenticated ? (
            /**
             * Step 2: Unauthenticated UX, in the look and language this device last
             * showed. Sign In and Sign Up both return to the SPA root; in local
             * development they open the persona picker instead. On a phone the card
             * is the whole screen.
             */
            <BrandCard>
              <Box>
                <Text as="h1" level="h4">
                  {t('auth.platformName')}
                </Text>
                <Text level="body2" styles={{ mt: 0.5 }}>
                  {t('auth.poweredBy')}
                </Text>
              </Box>

              <Stack spacing={1.25} styles={{ width: '100%' }}>
                <Button fullWidth size='lg' emphasis='solid' tone={signInTone} onClick={handleLogin}>
                  {t('auth.signIn')}
                </Button>
                <Button
                  fullWidth
                  emphasis={signInTone === 'secondary' ? 'outline' : 'plain'}
                  tone={signInTone}
                  styles={
                    signInTone === 'secondary'
                      ? { color: readableShade(palette.secondary.main, palette.background.paper) }
                      : undefined
                  }
                  onClick={handleRegister}
                >
                  {t('auth.signUp')}
                </Button>
              </Stack>
            </BrandCard>
          ) : showProfile ? (
            /**
             * Step 3: New user onboarding.
             * After creating the user record, we encourage completing profile before entering the dashboard.
             */
            <Box styles={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
              <Container maxWidth="lg" styles={{ py: 4 }}>
                <Paper elevation={2} styles={{ p: 3, mb: 3 }}>
                  <Text level="h5" gutterBottom tone="primary">
                    {t('profile.welcomeNewUser')}
                  </Text>
                  <Text level="body2" tone="text.secondary" paragraph>
                    {t('profile.newUserMessage')}
                  </Text>
                </Paper>

                <Suspense fallback={<MarkLoader size="page" />}>
                  <ProfilePage hideDeleteButton={true} />
                </Suspense>

                <Box styles={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                  <Button emphasis='solid' size='lg' onClick={handleProfileComplete}>
                    {t('profile.continueToDashboard')}
                  </Button>
                </Box>
              </Container>
            </Box>
          ) : bootstrapError ? (
            <ErrorFallback error={bootstrapError} onRetry={retryBootstrap} />
          ) : !adminChecked ? (
            /**
             * Step 4: Authenticated but role check in progress.
             * We avoid rendering the main app until admin routing decision is known.
             */
            <StartingScreen />
          ) : (
            /**
             * Steps 5 & 6: Authenticated app routing.
             * AppRouter's route tree decides whether /admin/* or / is rendered based on the URL.
             * We pass auth context via Outlet so downstream routes can access firstName, roles, etc.
             */
            <WebSocketProvider>
              <Outlet
                context={
                  {
                    firstName,
                    tenantRoles,
                    memberTenantIds,
                    onLogout: handleLogout,
                    isGlobalAdmin,
                    allowedPages,
                    navigation,
                    looks,
                    lookOutsideTenant,
                    reloadLooks,
                  } satisfies AuthContext
                }
              />
            </WebSocketProvider>
          )}
          {/* Dev-only role indicator / switcher (self-hides outside dev bypass). */}
          <DevModeBadge />
    </>
  );
}
